import React, { useState, useRef, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  Button,
  TextField,
  Box,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  FormHelperText,
  Modal,
  Fade,
  CircularProgress,
  Alert,
  Typography
} from '@mui/material';
import * as XLSX from 'xlsx';

const NotificationsForm = ({ onSendNotifications, users }) => {
  const { register, handleSubmit, control, watch, formState: { errors } } = useForm({
    defaultValues: {
      messageType: '',
      title: '',
      message: '',
      actionType: '',
      url: ''
    }
  });

  const messageType = watch('messageType');
  const actionType = watch('actionType');

  const [fileData, setFileData] = useState([]);
  const [text, setText] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isPopup, setIsPopup] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imageFileName, setImageFileName] = useState('');
  const [resultSummary, setResultSummary] = useState(null);
  const [submitError, setSubmitError] = useState('');

  const initialTemplates = [
    { label: 'Nombre', value: '#nombre ' },
    { label: 'Marca', value: '#marca ' },
    { label: 'Modelo', value: '#modelo ' },
    { label: 'Placa', value: '#placa ' }
  ];
  const [templates, setTemplates] = useState(initialTemplates);

  useEffect(() => {
    if (messageType !== 'excel') {
      setTemplates(initialTemplates);
      setFileData([]);
    }
  }, [messageType]);

  useEffect(() => {
    setIsPopup(actionType === 'popup');
  }, [actionType]);

  const textRef = useRef(null);

  const handleTextChange = (event) => {
    setText(event.target.value);
  };

  const insertTemplate = (template) => {
    const cursorPosition = textRef.current?.selectionStart || 0;
    const newText = text.slice(0, cursorPosition) + template + text.slice(cursorPosition);
    setText(newText);
    textRef.current?.focus();
    setTimeout(() => {
      if (textRef.current) {
        textRef.current.selectionStart = textRef.current.selectionEnd = cursorPosition + template.length;
      }
    }, 0);
  };

  const handleFileChange = (event) => {
    const reader = new FileReader();
    const file = event.target.files[0];

    setSubmitError('');
    setResultSummary(null);

    if (!file) {
      setSubmitError('No se seleccionó ningún archivo.');
      return;
    }

    reader.onload = (e) => {
      try {
        const workbook = XLSX.read(e.target.result, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const data = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName]);

        if (data.length === 0) {
          setSubmitError('El archivo está vacío.');
          return;
        }

        const flattenedData = data.flatMap((item) => {
          const cedulaExcel = String(item.cedula || item.Cedula || '').trim();

          if (!cedulaExcel) {
            return [];
          }

          return users
            .filter((u) => String(u.Cedula || '').trim() === cedulaExcel)
            .map((user) => ({
              ...item,
              devices: Array.isArray(user.devices) ? user.devices : [],
              cars: Array.isArray(user.cars) ? user.cars : [],
              ID: user.ID,
              Cedula: user.Cedula
            }));
        });

        const firstObjectKeys = Object.keys(data[0]).filter((key) => key !== '__rowNum__');

        setFileData(flattenedData);
        setTemplates(firstObjectKeys.map((key) => ({
          label: key,
          value: `#${key.toLowerCase()} `
        })));

        if (flattenedData.length === 0) {
          setSubmitError('No se encontraron usuarios coincidentes con las cédulas del Excel.');
        }
      } catch (error) {
        console.error('Error leyendo Excel:', error);
        setSubmitError('No se pudo leer el archivo Excel. Verifica el formato.');
      }
    };

    reader.readAsArrayBuffer(file);
  };

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    setImageFile(file || null);
    setImageFileName(file ? file.name : '');
  };

  const renderModal = () => (
    <Modal
      open={isSending}
      style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
    >
      <Fade in={isSending}>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          style={{ outline: 'none', width: '260px', backgroundColor: 'white', padding: '24px', borderRadius: '8px' }}
        >
          <CircularProgress />
          <Box mt={2} textAlign="center">
            <Typography variant="body1" fontWeight="bold">
              Enviando notificaciones
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Procesando lotes en el servidor...
            </Typography>
          </Box>
        </Box>
      </Fade>
    </Modal>
  );

  const buildFilesSending = (data) => {
    if (data.messageType === 'excel') {
      return fileData;
    }

    if (data.messageType === 'test') {
      return users.filter((item) => item.tipoCuentas === '2');
    }

    return users;
  };

  const onSubmit = async (data) => {
    setIsSending(true);
    setSubmitError('');
    setResultSummary(null);

    try {
      const filesSending = buildFilesSending(data);

      if (!filesSending.length) {
        throw new Error('No hay usuarios válidos para enviar la notificación.');
      }

      const currentUser = JSON.parse(localStorage.getItem('user'));
      const username = currentUser?.user?.username || 'desconocido';
      const actionUrl = data.url || data.actionUrl || '';

      const formData = new FormData();
      formData.append('tipomensaje', data.actionType || '');
      formData.append('tipo_accion', data.messageType || '');
      formData.append('titulo', data.title || '');
      formData.append('mensaje', text || '');
      formData.append('accion', actionUrl);
      formData.append('files', JSON.stringify(filesSending));
      formData.append('enviado_por', username);

      if (actionType === 'popup' && imageFile) {
        formData.append('url', imageFile);
      } else {
        formData.append('url', actionUrl);
      }

      const response = await fetch('https://app.silaba.com/NOTIFICATIONS/mercadeo/v1/', {
        method: 'POST',
        body: formData,
        redirect: 'follow'
      });

      const rawText = await response.text();
      let parsedResult = null;

      try {
        parsedResult = rawText ? JSON.parse(rawText) : null;
      } catch (jsonError) {
        console.error('Respuesta no JSON:', rawText);
        throw new Error('El servidor respondió con un formato inválido.');
      }

      if (!response.ok || !parsedResult?.success) {
        throw new Error(parsedResult?.error || parsedResult?.error_message || 'Error al enviar notificaciones.');
      }

      setResultSummary(parsedResult);

      if (typeof onSendNotifications === 'function') {
        onSendNotifications(parsedResult);
      }
    } catch (error) {
      console.error('Error:', error);
      setSubmitError(error.message || 'Error al enviar notificaciones.');
    } finally {
      setIsSending(false);
    }
  };

  const isSubmitDisabled =
    isSending ||
    users.length === 0 ||
    (messageType === 'excel' && fileData.length === 0);

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate autoComplete="off">
      <InputLabel id="active-users-label">Usuarios activos {users.length}</InputLabel>

      {submitError ? (
        <Alert severity="error" sx={{ mt: 2, mb: 1 }}>
          {submitError}
        </Alert>
      ) : null}

      {resultSummary ? (
        <Alert severity={resultSummary.status === 'partial' ? 'warning' : 'success'} sx={{ mt: 2, mb: 1 }}>
          <strong>
            {resultSummary.status === 'partial'
              ? 'Envío parcial completado.'
              : 'Notificaciones procesadas correctamente.'}
          </strong>
          <br />
          Usuarios válidos: {resultSummary.users_valid || 0} | Mensajes enviados a Expo: {resultSummary.messages_total || 0} | Lotes: {resultSummary.chunks_total || 0} | Tickets OK: {resultSummary.tickets_ok || 0} | Errores: {(resultSummary.tickets_error || 0) + (resultSummary.request_errors || 0)} | Tokens inválidos: {resultSummary.invalid_tokens || 0}
        </Alert>
      ) : null}

      <FormControl fullWidth margin="normal" error={!!errors.messageType}>
        <InputLabel id="message-type-label">Tipo Mensaje</InputLabel>
        <Controller
          name="messageType"
          control={control}
          rules={{ required: 'Tipo Mensaje es requerido' }}
          render={({ field }) => (
            <Select
              labelId="message-type-label"
              id="message-type"
              label="Tipo Mensaje"
              {...field}
            >
              <MenuItem value="todos">Todos los Usuarios</MenuItem>
              <MenuItem value="excel">Usar XLSX (Excel)</MenuItem>
              <MenuItem value="test">Usuarios de Prueba</MenuItem>
            </Select>
          )}
        />
        {errors.messageType && <FormHelperText>{errors.messageType.message}</FormHelperText>}
      </FormControl>

      <TextField
        fullWidth
        label="Título de la notificación"
        margin="normal"
        {...register('title', { required: 'Título es requerido' })}
        error={!!errors.title}
        helperText={errors.title ? errors.title.message : ''}
      />

      {messageType === 'excel' ? (
        <>
          <Box sx={{ display: 'flex', gap: '20px', justifyContent: 'center', mb: 2, mt: 2 }}>
            <Button variant="contained" component="label">
              Subir XLS
              <input
                type="file"
                hidden
                accept=".xlsx, .xls"
                onChange={handleFileChange}
              />
            </Button>
          </Box>

          <Box sx={{ display: 'flex', gap: '20px', justifyContent: 'center', mb: 2 }}>
            <InputLabel id="template-upload-label">Si desea otros campos para plantillas, puede cargar un XLSX</InputLabel>
          </Box>
        </>
      ) : null}

      {messageType !== 'excel' || (messageType === 'excel' && fileData.length > 0) ? (
        <Box sx={{ display: 'flex', gap: '20px', justifyContent: 'center', mb: 2, mt: 2, flexWrap: 'wrap' }}>
          {templates.map((template) => (
            <Button
              key={template.value}
              variant="contained"
              onClick={() => insertTemplate(template.value)}
              sx={{ borderRadius: '5px', backgroundColor: '#e1e4ea', color: '#333', fontWeight: 'bold' }}
            >
              {template.label}
            </Button>
          ))}
        </Box>
      ) : null}

      <Controller
        name="message"
        control={control}
        rules={{ required: 'El escribir un mensaje' }}
        render={({ field }) => (
          <TextField
            inputRef={textRef}
            multiline
            rows={6}
            value={text}
            onChange={(e) => {
              handleTextChange(e);
              field.onChange(e);
            }}
            fullWidth
            variant="outlined"
            margin="normal"
            label="Mensaje"
            placeholder="Escribe tu mensaje aquí..."
            error={!!errors.message}
            helperText={errors.message ? errors.message.message : ''}
          />
        )}
      />

      <FormControl fullWidth margin="normal">
        <InputLabel id="action-type-label">Tipo Acción</InputLabel>
        <Controller
          name="actionType"
          control={control}
          render={({ field }) => (
            <Select
              labelId="action-type-label"
              id="action-type"
              label="Tipo Acción"
              {...field}
            >
              <MenuItem value="popup">Popup (Imagen)</MenuItem>
              <MenuItem value="url">URL (Enlace externo)</MenuItem>
            </Select>
          )}
        />
      </FormControl>

      {isPopup ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <TextField
            fullWidth
            label="Imagen seleccionada"
            margin="normal"
            value={imageFileName}
            disabled
          />
          <Button
            variant="contained"
            component="label"
            size="large"
            sx={{
              height: '55px',
              minWidth: '155px',
              top: '3px'
            }}
          >
            Subir Imagen
            <input
              type="file"
              hidden
              accept="image/*"
              onChange={handleImageChange}
            />
          </Button>
        </Box>
      ) : (
        <TextField
          fullWidth
          label="URL de la Acción"
          margin="normal"
          {...register('url')}
        />
      )}

      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        <Button
          type="submit"
          disabled={isSubmitDisabled}
          variant="contained"
          color="primary"
          size="large"
        >
          {isSending ? 'Enviando...' : 'Enviar Notificaciones'}
        </Button>
        {isSending && renderModal()}
      </Box>
    </Box>
  );
};

export default NotificationsForm;
